/**
 * training-wizard-stepper.js
 */

import React from 'react';
import { Stepper, Step, StepLabel } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useLocation, matchPath } from 'react-router-dom';
import { Types } from 'utils/constants';
import { paths } from 'utils/routing';
import { shouldRenderMobileLayout } from 'utils/app-context';
import useRecordStore from 'contexts/record-store';
import { UserContext } from 'contexts/user/user-context';


const useStyles = makeStyles(theme => ({
  stepper: {
    backgroundColor: 'transparent',
    padding: 0,
    paddingTop: props => theme.spacing(props.paddingTop || 0.5),
    paddingBottom: props => theme.spacing(props.paddingBottom || 0),
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(-4),
      padding: theme.spacing(2),
    },
    '& .MuiStepLabel-label': {
      fontSize: theme.typography.sizes[1],
      marginTop: theme.spacing(1)
    },
    '& .MuiStepIcon-active': {
      transform: 'scale(1.3)',
    },
    '& .MuiStepLabel-active': {
      fontWeight: 'bold !important',
    }
  },
}));


export default function TrainingWizardStepper(props) {
  const {
    basePath,
    hideLabels,
    paddingTop,
    paddingBottom
  } = props;

  // styles
  const classes = useStyles(
    shouldRenderMobileLayout()
    ? {
      paddingTop: paddingTop ?? 0,
      paddingBottom: paddingBottom ?? 3,
    }
    : {
      paddingTop: paddingTop ?? 3,
      paddingBottom: paddingBottom ?? 1
    });

  const { user } = React.useContext(UserContext);

  // route params
  const location = useLocation();
  const match = matchPath(location.pathname, {
    path: basePath + paths.training.base + paths.training.wizardPattern
  });
  // const wzType = match ? match.params.wzType : null;
  const step = match ? parseInt(match.params.step) : null;
  const type = match ? match.params.type : null;
  const id = match ? match.params.id : null;

  // record store
  const recordStore = useRecordStore();
  const record = recordStore.useRecord(Types.Record.getId(type), id).ornull;
  const eventId = type === 'event' ? id : record ? record.get().eventId : 0;
  const exerciseId = type === 'exercise' ? id : record ? record.get().exercises[0] : 0;
  const eventRecord = recordStore.useRecord(Types.Record.EVENT, eventId).ornull;
  const exerciseRecord = recordStore.useRecord(Types.Record.EXERCISE, exerciseId).ornull;

  // training completion states
  const hasExers = eventRecord && eventRecord.get().exercises.length > 0;
  const hasCompletions = exerciseRecord?.get()?.fullStats?.complete > 0;


  const steps = user.role.isHandler
    ? {
      1: <>Select<br/>Event</>,
      2: <>Add<br/>Exercise</>,
      3: <>Complete<br/>Exercise</>,
      4: <>Review<br/>& Edit</>
    }
    : {
      1: <>Select<br/>Event</>,
      2: <>Add<br/>Exercise</>,
      3: <>Review<br/>& Edit</>
    }


  const isCompleted = (key, step) => {
    if (step <= key) return false;
    if (key === '2') return hasExers;
    if (user.role.isHandler && key === '3') return hasCompletions;
    return step > key;
  }


  return (
    <>
      {step &&
      <Stepper
        activeStep={step-1}
        alternativeLabel
        className={classes.stepper}
        data-testid="wizard-stepper"
      >
        {Object.keys(steps).map(key => {
          return(
          <Step
            key={key}
            completed={isCompleted(key, step)}
          >
            <StepLabel>
              {!hideLabels ? steps[key] : ''}
            </StepLabel>
          </Step>
        ); })}
      </Stepper>}
    </>
  );
}